import React, { Fragment, useCallback, useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import {
  Button, Col, Container, DropdownItem, DropdownMenu, DropdownToggle,
  Form, FormGroup, Input, Label, Media, Row, UncontrolledDropdown,
} from "reactstrap";
import { MoreVertical, PlusCircle } from "react-feather";
import { FaPen, FaTrashAlt } from "react-icons/fa";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";
import { Spinner } from "../../../../AbstractElements";
import CommonModal from "../../../UiKits/Modals/common/modal";
import { baseURL } from "../../../../Services/api/baseURL";
import Loader from "../../../Loader/Loader";
import useRbac from "../../../../Hooks/useRbac";

const NOTICE_TYPES = [
  { value: "offer", label: "Offer" },
  { value: "announcement", label: "Announcement" },
  { value: "warning", label: "Warning" },
  { value: "info", label: "Info" },
];

const initialForm = {
  title: "",
  description: "",
  code: "",
  validTill: "",
  icon: "🎉",
  type: "offer",
  showCode: true,
  status: "inactive",
};

const NOTICE_ICONS_BY_TYPE = {
  offer: [
    { emoji: "🎉", label: "Party" },
    { emoji: "🎁", label: "Gift" },
    { emoji: "💰", label: "Money" },
    { emoji: "💸", label: "Cash" },
    { emoji: "🏷️", label: "Tag" },
    { emoji: "🔥", label: "Fire" },
    { emoji: "💎", label: "Diamond" },
    { emoji: "🛒", label: "Cart" },
    { emoji: "🛍️", label: "Shopping" },
    { emoji: "🍻", label: "Cheers" },
    { emoji: "🥂", label: "Toast" },
    { emoji: "✨", label: "Sparkles" },
  ],
  announcement: [
    { emoji: "📢", label: "Announcement" },
    { emoji: "📣", label: "Megaphone" },
    { emoji: "🔔", label: "Bell" },
    { emoji: "📌", label: "Pin" },
    { emoji: "🚀", label: "Launch" },
    { emoji: "⭐", label: "Star" },
    { emoji: "🎯", label: "Target" },
    { emoji: "📰", label: "News" },
  ],
  info: [
    { emoji: "ℹ️", label: "Info" },
    { emoji: "💡", label: "Tip" },
    { emoji: "📝", label: "Note" },
    { emoji: "✅", label: "Check" },
    { emoji: "👉", label: "Pointer" },
    { emoji: "🔍", label: "Search" },
    { emoji: "📖", label: "Guide" },
    { emoji: "💬", label: "Message" },
  ],
  warning: [
    { emoji: "⚠️", label: "Warning" },
    { emoji: "❗", label: "Alert" },
    { emoji: "🚨", label: "Urgent" },
    { emoji: "⛔", label: "Stop" },
    { emoji: "🛑", label: "Halt" },
    { emoji: "⏰", label: "Time" },
    { emoji: "🔴", label: "Critical" },
    { emoji: "❌", label: "Error" },
  ],
};


const NoticeTable = () => {
  const userRole = JSON.parse(localStorage.getItem("role_name"));
  const { can, isSuperAdmin } = useRbac();

  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleDelet, setToggleDelet] = useState(false);

  const [addModal, setAddModal] = useState(false);
  const [statusModal, setStatusModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [form, setForm] = useState(initialForm);

  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  const getToken = () => JSON.parse(localStorage.getItem("token"));

  const resetForm = () => setForm(initialForm);

  const toggleAddModal = () => {
    setAddModal((p) => !p);
    setEditData(null);
    resetForm();
  };

  const handleSearch = (e) => setSearchTerm(e.target.value);

  const handleField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // ---------- API ----------
  const fetchNotices = async (page = 1, limit = perPage) => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${baseURL}/api/admin/get-notices?page=${page}&limit=${limit}`,
        { headers: { Authorization: `${getToken()}` } }
      );
      setData(res.data.data || []);
      setTotalRows(res.data.total || 0);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const postNotice = async () => {
    try {
      await axios.post(`${baseURL}/api/admin/add-notice`, form, {
        headers: { Authorization: `${getToken()}` },
      });
      toggleAddModal();
      fetchNotices(1);
      Swal.fire({ title: "Notice Added!", icon: "success", confirmButtonColor: "#d3178a" });
    } catch (err) {
      console.log(err);
      Swal.fire({ title: "Failed to add notice", icon: "error", confirmButtonColor: "#d3178a" });
    }
  };

  const editNotice = async (id) => {
    try {
      await axios.patch(`${baseURL}/api/admin/update-notice/${id}`, form, {
        headers: { Authorization: `${getToken()}` },
      });
      toggleAddModal();
      fetchNotices(1);
      Swal.fire({ title: "Notice Updated!", icon: "success", confirmButtonColor: "#d3178a" });
    } catch (err) {
      console.log(err);
    }
  };

  const toggleNoticeStatus = async () => {
    try {
      const newStatus = editData.status === "active" ? "inactive" : "active";
      await axios.patch(
        `${baseURL}/api/admin/update-notice-status/${editData._id}`,
        { status: newStatus },
        { headers: { Authorization: `${getToken()}` } }
      );
      setStatusModal(false);
      setEditData(null);
      fetchNotices(1);
    } catch (err) {
      console.log(err);
    }
  };

  const deleteNotice = async () => {
    try {
      await axios.delete(`${baseURL}/api/admin/delete-notice/${editData._id}`, {
        headers: { Authorization: `${getToken()}` },
      });
      setDeleteModal(false);
      setEditData(null);
      fetchNotices(1);
      Swal.fire({ title: "Notice Deleted!", icon: "success", confirmButtonColor: "#d3178a" });
    } catch (err) {
      console.log(err);
      Swal.fire({ title: err?.response?.data?.message || "Failed to delete notice", icon: "error", confirmButtonColor: "#d3178a" });
    }
  };

  // ---------- pagination ----------
  const handlePageChange = (page) => fetchNotices(page, perPage);
  const handlePerRowsChange = async (newPerPage, page) => {
    setPerPage(newPerPage);
    fetchNotices(page, newPerPage);
  };

  useEffect(() => {
    fetchNotices(1);
    // eslint-disable-next-line
  }, []);

  // ---------- handlers ----------
  const handleSubmit = () => {
    if (!form.title || !form.description) {
      Swal.fire({ title: "Title and description are required", icon: "warning", confirmButtonColor: "#d3178a" });
      return;
    }
    if (editData) editNotice(editData._id);
    else postNotice();
  };

  const openEdit = (row) => {
    setEditData(row);
    setForm({
      title: row.title || "",
      description: row.description || "",
      code: row.code || "",
      validTill: row.validTill || "",
      icon: row.icon || "🎉",
      type: row.type || "offer",
      showCode: row.showCode ?? true,
      status: row.status || "inactive",
    });
    setAddModal(true);
  };

  const handleRowSelected = useCallback((state) => setSelectedRows(state.selectedRows), []);

  // ---------- columns ----------
  const columns = [
    {
      name: "Title",
      selector: (row) => row.title,
      sortable: true,
      center: true,
      cell: (row) => (
        <span>
          <span className="me-1">{row.icon}</span>
          {row.title}
        </span>
      ),
    },
    {
      name: "Description",
      selector: (row) => row.description,
      sortable: true,
      center: true,
    },
    {
      name: "Code",
      selector: (row) => row.code,
      center: true,
      cell: (row) => row.code || "—",
    },
    {
      name: "Type",
      selector: (row) => row.type,
      sortable: true,
      center: true,
      cell: (row) => <span className="capitalize">{row.type}</span>,
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      center: true,
      cell: (row) => (
        <p className={`text-${row.status === "inactive" ? "[#ff0000]" : "[#008800]"} mb-0`}>
          {row.status === "active" ? "Active" : "In-Active"}
        </p>
      ),
    },
    {
      name: "Created",
      selector: (row) => row.createdAt,
      sortable: true,
      center: true,
      cell: (row) => moment(row.createdAt).format("DD/MM/YYYY"),
    },
    {
      name: "Updated",
      selector: (row) => row.updatedAt,
      sortable: true,
      center: true,
      cell: (row) => moment(row.updatedAt).format("DD/MM/YYYY"),
    },
    {
      name: "Actions",
      center: true,
      omit: !can('notices.update'),
      cell: (row) => (
        <UncontrolledDropdown className="action_dropdown">
          <DropdownToggle className="action_btn">
            <MoreVertical color="#000" size={16} />
          </DropdownToggle>
          <DropdownMenu>
            {can('notices.update') && <DropdownItem onClick={() => openEdit(row)}>
              Edit <FaPen />
            </DropdownItem>}
            {can('notices.update') && <DropdownItem
              onClick={() => {
                setEditData(row);
                setStatusModal(true);
              }}
            >
              {row.status === "active" ? "Deactivate" : "Activate"}
            </DropdownItem>}
            {isSuperAdmin && <DropdownItem
              className="text-danger"
              onClick={() => {
                setEditData(row);
                setDeleteModal(true);
              }}
            >
              Delete <FaTrashAlt />
            </DropdownItem>}
          </DropdownMenu>
        </UncontrolledDropdown>
      ),
    },
  ];

  const filtered = data.filter((it) =>
    `${it.title} ${it.description} ${it.code}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Fragment>
      <Row xxl={12} className="pb-4">
        <div className="d-flex justify-content-between align-items-center">
          {/* <H4>Notice List</H4> */}
           <h5 className="fw-bolder">Notice List</h5>
          <div className="file-content">
            <Media>
              <div className="mb-0 form-group position-relative search_outer d-flex align-items-center">
                <i className="fa fa-search"></i>
                <input
                  className="form-control border-0"
                  value={searchTerm}
                  onChange={handleSearch}
                  type="text"
                  placeholder="Search..."
                />
              </div>
              {can('notices.create') && (
                <Media body className="text-end ms-3">
                  <Button className="btn btn-primary d-flex align-items-center" onClick={toggleAddModal}>
                    <PlusCircle />
                    Add Notice
                  </Button>
                </Media>
              )}
            </Media>
          </div>
        </div>
      </Row>

      {data.length !== 0 ? (
        <DataTable
          data={filtered}
          columns={columns}
          pagination
          paginationServer
          progressPending={loading}
          progressComponent={<Loader />}
          paginationTotalRows={totalRows}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          onSelectedRowsChange={handleRowSelected}
          clearSelectedRows={toggleDelet}
        />
      ) : (
        <Col xxl="12" key="loader" className="flex justify-center items-center">
          <div className="loader-box">
            <Spinner />
          </div>
        </Col>
      )}


      {/* Add / Edit Modal */}
      <CommonModal
        isOpen={addModal}
        title={editData ? "Edit Notice" : "Add New Notice"}
        className="store_modal"
        toggler={toggleAddModal}
        size="lg"
      >
        <Container>
          <Form>
            <Col xxl={12}>
              <FormGroup>
                <Label className="font-medium text-base">
                  Title <span className="text-danger">*</span>
                </Label>
                <Input
                  value={form.title}
                  onChange={(e) => handleField("title", e.target.value)}
                  placeholder="e.g. This week's deal"
                />
              </FormGroup>
            </Col>

            <Col xxl={12}>
              <FormGroup>
                <Label className="font-medium text-base">
                  Description <span className="text-danger">*</span>
                </Label>
                <Input
                  value={form.description}
                  onChange={(e) => handleField("description", e.target.value)}
                  placeholder="e.g. Get $5 OFF"
                />
              </FormGroup>
            </Col>

 <Row>
              <Col xxl={6} md={6}>
                <FormGroup>
                  <Label className="font-medium text-base">Type</Label>
                  {/* <select
                    className="form-control"
                    value={form.type}
                    onChange={(e) => handleField("type", e.target.value)}
                  >
                    {NOTICE_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select> */}
                  <select
                    className="form-control"
                    value={form.type}
                    onChange={(e) => {
                      const newType = e.target.value;
                      const defaultIcon = NOTICE_ICONS_BY_TYPE[newType]?.[0]?.emoji || "🎉";
                      setForm((prev) => ({ ...prev, type: newType, icon: defaultIcon }));
                    }}
                  >
                    {NOTICE_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </FormGroup>
              </Col>
              {/* <Col xxl={6} md={6}>
                <FormGroup>
                  <Label className="font-medium text-base">Icon (emoji)</Label>
                  <Input
                    value={form.icon}
                    onChange={(e) => handleField("icon", e.target.value)}
                    placeholder="🎉"
                  />
                </FormGroup>
              </Col> */}
              <Col xxl={6} md={6}>
                <FormGroup>
                  <Label className="font-medium text-base">Icon</Label>
                  <div className="border rounded p-2 bg-white" style={{ minHeight: "60px" }}>
                    <div className="flex flex-wrap gap-1">
                      {(NOTICE_ICONS_BY_TYPE[form.type] || []).map((item) => (
                        <button
                          key={item.emoji}
                          type="button"
                          title={item.label}
                          onClick={() => handleField("icon", item.emoji)}
                          className={`text-xl rounded transition-all hover:bg-[#fff0f7] ${form.icon === item.emoji ? "ring-2 ring-[#d3178a] bg-[#fff0f7] scale-110" : ""
                            }`}
                          style={{
                            width: "36px",
                            height: "36px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "none",
                            cursor: "pointer",
                            background: form.icon === item.emoji ? "#fff0f7" : "transparent",
                          }}
                        >
                          {item.emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                  <small className="text-[#1D2433A6]">
                    Selected: <span className="text-base">{form.icon || "none"}</span>
                  </small>
                </FormGroup>
              </Col>
</Row>
 <Row>
            <Col xxl={6}>
              <FormGroup>
                <Label className="font-medium text-base">Code (optional)</Label>
                <Input
                  value={form.code}
                  onChange={(e) => handleField("code", e.target.value.toUpperCase())}
                  placeholder="GBZ5"
                />
              </FormGroup>
            </Col>

            <Col xxl={6}>
              <FormGroup>
                <Label className="font-medium text-base">Footer (text)</Label>
                <Input
                  value={form.validTill}
                  onChange={(e) => handleField("validTill", e.target.value)}
                  placeholder="Valid till Sunday.."
                />
              </FormGroup>
            </Col>
</Row>
            {/* <Col xxl={12}>
              <FormGroup check className="mb-2">
                <Label check>
                  <Input
                    type="checkbox"
                    checked={form.showCode}
                    onChange={(e) => handleField("showCode", e.target.checked)}
                  />{" "}
                  Show code button on frontend
                </Label>
              </FormGroup>
            </Col>*/}

            <Col xxl={12}>
              <FormGroup className="mb-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!form.showCode}
                    onChange={(e) => handleField("showCode", e.target.checked)}
                    className="w-4 h-4 cursor-pointer accent-[#d3178a]"
                  />
                  <span>Show code button on frontend</span>
                </label>
              </FormGroup>
            </Col>

            <Col xxl={12}>
              <FormGroup>
                <Label className="font-medium text-base">Status</Label>
                <div className="flex flex-row gap-4">
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      checked={form.status === "active"}
                      onChange={() => handleField("status", "active")}
                    />
                    Active
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      checked={form.status === "inactive"}
                      onChange={() => handleField("status", "inactive")}
                    />
                    Inactive
                  </label>
                </div>
                <small className="text-[#1D2433A6]">
                  Activating this notice will deactivate the currently active one.
                </small>
              </FormGroup>
            </Col>

            <Row>
              <Col xxl={12} className="text-end">
                <Button
                  type="button"
                  onClick={handleSubmit}
                  className="cursor-pointer bg-[#d3178a] font-medium w-40 mt-4 px-2 py-2 rounded-2xl text-white flex justify-center items-center"
                  style={{ border: "none" }}
                >
                  {editData ? "Update Notice" : "Save Notice"}
                </Button>
              </Col>
            </Row>
          </Form>
        </Container>
      </CommonModal>

      {/* Status Toggle Confirm */}
      <CommonModal
        isOpen={statusModal}
        title=""
        toggler={() => setStatusModal(false)}
        size="md"
      >
        <Container className="flex flex-col justify-center items-center">
          <p>
            {editData?.status === "active"
              ? "Deactivate this notice?"
              : "Activate this notice? Any other active notice will be deactivated."}
          </p>
          <div className="flex flex-row space-x-8">
            <div
              onClick={() => setStatusModal(false)}
              className="border-1 border-[#ff0000] px-2 py-2 text-[#ff0000] rounded-xl cursor-pointer"
            >
              Cancel
            </div>
            <div
              onClick={toggleNoticeStatus}
              className="bg-[#d3178a] px-4 py-2 text-white rounded-xl cursor-pointer"
            >
              Yes
            </div>
          </div>
        </Container>
      </CommonModal>

      {/* Delete Confirm (super admin only) */}
      <CommonModal
        isOpen={deleteModal}
        title=""
        toggler={() => setDeleteModal(false)}
        size="md"
      >
        <Container className="flex flex-col justify-center items-center">
          <p>
            Delete notice "{editData?.title}"? This cannot be undone.
          </p>
          <div className="flex flex-row space-x-8">
            <div
              onClick={() => setDeleteModal(false)}
              className="border-1 border-[#1D2433A6] px-2 py-2 text-[#1D2433A6] rounded-xl cursor-pointer"
            >
              Cancel
            </div>
            <div
              onClick={deleteNotice}
              className="bg-[#ff0000] px-4 py-2 text-white rounded-xl cursor-pointer"
            >
              Delete
            </div>
          </div>
        </Container>
      </CommonModal>
    </Fragment>
  );
};

export default NoticeTable;